/* global $, angular, _ */

/**
 * Controller, resolves and related functionality for the main view.
 */
import {ContentUtils} from '../../js/atex/common';
import moment from 'moment';
import Debug from 'ace-ui/ace-debug';

const COMPONENT_NAME = 'MainViewController';
const debug = new Debug (COMPONENT_NAME);
const LATEST_REVISION = -42;
const PUBLIC_VIEW = 'acePublic';
const WORKFLOW_SUBSCRIPTION = 'MAIN_WORKFLOW';
const UPDATE_SUBSCRIPTION = 'MAIN_UPDATE';

const currentRevisionNumber = (revision) => {
  return revision
    ? revision.substring(revision.lastIndexOf('/') + 1, revision.length - 1)
    : undefined;
};

export default function ContentDeveloperMainController (
  $location, $q, $rootScope, $route, $scope,
  $timeout, $translate, $uibModal, $window,
  AuthorizationService, ClipboardService,
  CloneContentService, CodeWorkflow,
  CompareService, ConflictService,
  ContentEditor, ContentEventService,
  ContentService, ContentStateService,
  ContentVersionModal, ErrorMessagingService,
  KeyboardShortcutService, loadData,
  PreviewService, Progress,
  RecentContentService, UserDataService,
  UserProfileModal, ValidationService,
  ViewService, WorkflowService,
  WorkspaceService) {

  ErrorMessagingService.clearErrors();

  var draftCreated = false;

  loadData.metadata = loadData.metadata
    || loadData.content
    && loadData.content.operations
    && loadData.content.operations.find(function (op) {
      return op._type === 'aceContentMetadata';
    });

  debug.log ('Content', loadData.content);
  debug.log ('Workflow', loadData.workflow);
  debug.log ('Metadata', loadData.metadata);
  debug.log ('Revision', loadData.revision);
  debug.log ('Cloneable', loadData.isCloneable);

  $scope.autosaved = loadData.autosaved || false;
  $scope.collapseWorkflowHistory = true;
  $scope.copyInProgress = false;

  $scope.currentRevision = loadData.revision;
  $scope.currentRevisionNumber = parseInt(currentRevisionNumber ($scope.currentRevision));

  $scope.hasPreviousVersion = loadData.hasPreviousVersion;
  $scope.inProgress = new Progress();
  debug.status && (window.checkProgress = () => $scope.inProgress.check(true));
  $scope.isCloneable = !!loadData.isCloneable;
  $scope.cloneOf = $location.search ().cloneOf;
  $scope.isDestroyed = false;
  $scope.isDirty = false;
  $scope.isHidden = atex.aceui.ObjectUtils.getByPath(loadData, 'metadata/aspects/aceContentState/hidden');
  $scope.isLoading = false;
  $scope.showSpinner = false;
  $scope.isPreviewable = loadData.isPreviewable;
  $scope.publicView = PUBLIC_VIEW;
  $scope.workflow = loadData.workflow;
  $scope.metadata = loadData.metadata;
  $scope.userId = loadData.userId;
  $scope.publishTime = loadData.metadata
    && loadData.metadata.aspects.aceTimeState
    && loadData.metadata.aspects.aceTimeState.views[PUBLIC_VIEW]
    || {'onTime': undefined, 'offTime': undefined};

  // Aliases

  $scope.aliases = [];
  const aliases = $scope.metadata?.aspects?.aceAliases?.aliases || {};
  Object.keys (JSON.parse (JSON.stringify (aliases), (key, value) => {
    return !key.endsWith('_type') ? value : undefined;
  })).forEach ((namespace) => {
    $scope.aliases = $scope.aliases || [];
    aliases [namespace].forEach (id => {
      if (loadData.content.system.id !== `${namespace}/${id}`) {
        $scope.aliases.push (`${namespace}/${id}`)
      }
    })
  });

  // Workflow...

  debug.log ('Subscribing to workflow events and update events');

  if ($scope.workflow && $scope.workflow.state && $scope.workflow.state.invalid) {
    ErrorMessagingService.sendError(atex.aceui.error.Error({
      cause: 'Warning: The workflow state of this content no longer exists. The workflow currently in progress needs to be aborted in order to continue work!',
      severity: atex.aceui.error.severities.MINIMAL,
      origin: 'Main View Controller'
    }));
  }

  ContentEventService.addSubscriptions({
    [WORKFLOW_SUBSCRIPTION]: {
      eventType: 'WORKFLOW'
    },
    [UPDATE_SUBSCRIPTION]: {
      eventType: 'UPDATE'
    }
  });

  let unsubscribeWorkflow = ContentEventService.subscribe (WORKFLOW_SUBSCRIPTION, (eventData) => {
    // Irrelevant for new content
    if (!$scope.currentRevision) {
      return;
    }

    debug.log ('Incoming workflow event:', eventData, eventData.current.versionId, $scope.currentVersion, $scope.inProgress.check());

    let localRevision = parseInt(currentRevisionNumber($scope.currentRevision));

    if (!$scope.inProgress.check ()
        && eventData.system.mainAlias === $scope.content.system.id
        && eventData.current.revision !== localRevision) {

      ErrorMessagingService.sendError(atex.aceui.error.Error({
        cause: 'Warning: The workflow state of this content has been updated!',
        severity: atex.aceui.error.severities.MINIMAL,
        origin: 'Main View Controller'
      }), {
        'code.reload': () => $route.reload()
      });
    }
  });

  let unsubscribeUpdate = ContentEventService.subscribe (UPDATE_SUBSCRIPTION, (eventData) => {
    // Irrelevant for new content
    if (!$scope.currentRevision) {
      return;
    }

    let localRevision = parseInt(currentRevisionNumber($scope.currentRevision));

    if (!$scope.inProgress.check ()
        && eventData.system.mainAlias === $scope.content.system.id
        && eventData.current.revision !== localRevision) {

      ErrorMessagingService.sendError(atex.aceui.error.Error({
        cause: 'Warning: This content has been updated by someone else!',
        severity: atex.aceui.error.severities.MINIMAL,
        origin: 'Main View Controller'
      }), {
        'code.reload': () => $route.reload()
      });
    }
  });

  $scope.$on('$destroy', function () {
    unsubscribeWorkflow();
    unsubscribeUpdate();

    $scope.isDestroyed = true;
    $window.onbeforeunload = undefined;
  });

  var beforeUnloadFunction = function (e) {
    var confirmationMessage = 'Autosave not performed, you might lose some data!';

    if ($scope.isDirty) {
      // If we want to show pop-up on exit, most modern browser doesn't use the custom string
      (e || window.event).returnValue = confirmationMessage; //Gecko + IE
      return confirmationMessage; //Webkit, Safari, Chrome
    }
  };

  var routeChangeFunction = function () {
    if ($scope.isDirty) {
      saveDraft().catch(function (error) {
        console.warn('Error in autosave on route change!', error);
      });
    }
  };

  $window.onbeforeunload = beforeUnloadFunction;
  $scope.$on('$routeChangeStart', routeChangeFunction);

  $scope.changeStart = function () {
    $scope.isDirty = true;
  };

  if (loadData.autosaved) {
    var recentContent = RecentContentService.getRecentContent(loadData.content.system.id);
    if (!recentContent || !recentContent.autosaved) {
      var timestamp = moment(loadData.content.system.modificationTime).fromNow();
      ErrorMessagingService.sendError(atex.aceui.error.Error({
        cause: 'This is an autosaved draft created ' + timestamp,
        severity: atex.aceui.error.severities.MINIMAL,
        origin: 'Main View Controller'
      }));
    }
  }

  $scope.mode = loadData.mode;
  $scope.isEditMode = $scope.mode === 'edit';

  $scope.hasPublicVersion = false;
  $scope.isPublishable = false;
  $scope.contentStatusTitle = undefined;

  $scope.contentHistory = undefined;
  $scope.contentHistoryActive = false;
  $scope.contentHistoryError = undefined;

  $scope.currentField = 0;

  var existingEditorState = RecentContentService
    .getEditorStateFor(atex.aceui.ObjectUtils.getByPath(loadData, 'content/system/id'));

  var hasContentId = typeof loadData.content.system.id !== 'undefined';

  $scope.content = typeof loadData.content !== 'undefined' ? loadData.content : atex.aceui.Content();
  $scope.template = loadData.template;

  $scope.currentVersion = atex.aceui.ObjectUtils.getByPath($scope.content, 'system/version');
  $scope.validationContext = ValidationService.instantiate();

  const confirmDispencer = (confirmRestore) => {
    let resolve;

    if (confirmRestore) {
      resolve = {
        confirmText: ($translate) => $translate('content-info.delete.confirm.restore.label'),
        cancelText: ($translate) => $translate('content-info.delete.confirm.cancel.label'),
        message: ($translate) => $translate('content-info.delete.confirm.restore.text'),
        title: ($translate) => $translate('content-info.delete.confirm.restore.label')
      };
    } else {
      resolve = {
        confirmText: ($translate) => $translate('content-info.delete.confirm.delete.label'),
        cancelText: ($translate) => $translate('content-info.delete.confirm.cancel.label'),
        message: ($translate) => $translate('content-info.delete.confirm.delete.text'),
        title: ($translate) => $translate('content-info.delete.confirm.delete.label')
      };
    }

    const modal = $uibModal.open({
      animation: false,
      backdrop: 'static',
      keyboard: false,
      component: 'confirmDialog',
      resolve
    });

    return modal.result;
  };

  $scope.cloneContent = function () {
    CloneContentService.clone ($scope.content.system.id);
  };

  $scope.softDeleteContent = function () {
    confirmDispencer()
      .then(() => {
        $scope.hasSaved = true;
        return ContentStateService.updateHiddenState({
          alias: $scope.content.system.id,
          hidden: true
        }).then (() => {
          RecentContentService.updateHiddenState($scope.content.system.id, true);
          $route.reload();
        });
      }, () => {
        $scope.hasSaved = false;
      });
  };

  $scope.restoreContent = function () {
    confirmDispencer(true)
      .then(() => {
        return ContentStateService.updateHiddenState({
          alias: $scope.content.system.id,
          hidden: false
        }).then (() => {
          RecentContentService.updateHiddenState($scope.content.system.id, false);
          $route.reload();
        });
      }, () => {});
  };

  const notInContentHistory = (version) => {
    return !$scope.contentHistory
      || !$scope.contentHistory.versions
      || !$scope.contentHistory.versions.find((v) => v.version === version);
  };

  $scope.formatDate = (date, format) => {
    return moment(date).format(format);
  };

  const userDataRequestpromises = {};

  const userDataFor = (principalId) => {
    userDataRequestpromises[principalId] = userDataRequestpromises[principalId]
      || UserDataService.getUserData(principalId);

    return userDataRequestpromises[principalId]
      .then((userDataResult) => {
        return atex.aceui.ObjectUtils.getByPath(userDataResult, 'userData/acePassport');
      });
  };

  const packageWorkflowHistory = () => {
    if ($scope.metadata) {
      let workflows = atex.aceui.ObjectUtils.getByPath($scope, 'metadata/aspects/aceWorkflowState/workflows');

      if (workflows) {
        let workflowDefinitions = {};
        $scope.workflowHistory = workflows.map((workflow) => {
          let result = Object.assign({}, workflow);

          workflowDefinitions[workflow.workflowId] =
            workflowDefinitions[workflow.workflowId]
            || WorkflowService.getWorkflow({ workflowId: workflow.workflowId });

          workflowDefinitions[workflow.workflowId]
            .then ((definition) => {
              result.workflowDefinition = definition;
            });

          userDataFor(workflow.principal || 'nobody')
            .then((passport) => {
              result.principalPassport = passport;
            });

          if (workflow.status === 'IN_PROGRESS') {
            result.open = true;
            result.finisher = undefined;
          } else {
            result.closed = true;
            result.endDate = workflow.events[workflow.events.length - 1].date;
            result.finisher = workflow.events[workflow.events.length - 1].principal || 'nobody';

            userDataFor(result.finisher)
              .then((passport) => {
                result.finisherPassport = passport;
              });
          }

          for (let i = 0; i < workflow.events.length; i++) {
            let event = result.events[i];

            event.date = moment(event.date).format('L - LTS');

            if (i === 0) {
              event.previousState = 'N/A';
            } else {
              event.previousState = workflow.events[i - 1].state;
            }

            if (notInContentHistory(event.versionId)) {
              event.versionNotAvailable = true;
            }

            if (event.state === undefined) {
              event.state = '<N/A>';
            }

            userDataFor(event.principal || 'nobody')
              .then((passport) => {
                event.principalPassport = passport;
              });
          }

          return result;
        });
      }
    }
  };

  const readContentHistory = function () {
    $scope.inProgress.increment('readContentHistory');
    $scope.hasPublicVersion = false;
    $scope.selectCounter = 0;

    $scope.currentVersionIsPublic = false;

    delete $scope.contentStatusTitle;

    return ContentService.getContentHistory({
      alias: $scope.content.system.id,
      fullTypeInfo: true,
      atLeastRevision: currentRevisionNumber($scope.currentRevision) || LATEST_REVISION
    })
      .then(function (historyResult) {
        $scope.contentHistory = historyResult.history || { versions: [] };

        $scope.contentHistory.versions.forEach(function (version) {
          version.views.forEach(function (view) {
            if (view === PUBLIC_VIEW) {
              $scope.hasPublicVersion = true;
              $scope.contentStatusTitle = 'A previous version of this article is published';

              if (version.version === $scope.currentVersion) {
                $scope.currentVersionIsPublic = true;

                if ($scope.isEditMode !== true) {
                  $scope.contentStatusTitle = 'This article is published';
                }
              }
            }
          });

          userDataFor(version.creatorId)
            .then((passport) => {
              version.passport = passport;
            });
        });

        $scope.isPublishable = !$scope.currentVersionIsPublic;

        if ($scope.contentHistory.versions.length > 0 && $scope.isEditMode) {
          var lastSavedVersion = $scope.contentHistory.versions[$scope.contentHistory.versions.length - 1];

          includeDraftVersion();

          if (lastSavedVersion.creationTime > existingEditorState.draftCreationTime) {
            var message = 'Your draft conflicts with a newer version created by ' + lastSavedVersion.creatorId
              + ', when saving the conflict needs to be resolved';

            var error = atex.aceui.error.Error({
              cause: message,
              severity: atex.aceui.error.severities.MINOR,
              origin: 'Content Developer Main Controller: saveToServer'
            });

            return ErrorMessagingService.sendError(error);
          }
        }

        $scope.contentHistory.contentType = loadData.type;
      })
      .catch(function (error) {
        console.warn('Error while retrieving content information!', error);

        $scope.contentHistory = {
          contentType: loadData.type
        };

        $scope.contentHistoryError = error;
        $scope.contentHistoryError.message = 'Failed to load content information';
      })
      .then(function () {
        $scope.inProgress.decrement ('readContentHistory');
      });
  };

  var includeDraftVersion = function () {
    var currUser = loadData.workspace.split('-')[0];
    $scope.currentVersion = '-1';

    var draftVersion = {
      version: '-1',
      revision: {},
      creationTime: existingEditorState.draftCreationTime || Date.now(),
      creatorId: currUser,
      views: [],
      mode: 'Draft'
    };

    userDataFor(draftVersion.creatorId)
      .then((passport) => {
        draftVersion.passport = passport;
      });

    if (typeof existingEditorState.draftCreationTime === 'undefined') {
      RecentContentService.updateEditorStateFor(loadData.content.system.id, {
        draftCreationTime: Date.now()
      });
    }

    $scope.contentHistory.versions.push(draftVersion);
  };

  if (loadData.hasPreviousVersion) {
    RecentContentService.addContent(loadData.content.system.id, true, $scope.autosaved);
    RecentContentService.updateHiddenState(loadData.content.system.id, $scope.isHidden);

    readContentHistory()
      .then(() => {
        packageWorkflowHistory();
      });
  }

  if ($scope.isEditMode && hasContentId === true) {
    RecentContentService.updateEditorStateFor(loadData.content.system.id, {
      mode: 'edit'
    });
  }

  $rootScope.contentObjectInEdit = JSON.parse(JSON.stringify(loadData.content));
  if (hasContentId) {
    $rootScope.contentInEdit = loadData.content.system.id;
  }

  $scope.startEditing = function () {
    ContentEditor.editContent($route.current.params.contentId || $scope.content.system.id);
  };

  $scope.cancelEditing = function () {
    if (hasContentId) {
      RecentContentService.updateEditorStateFor(loadData.content.system.id, {
        mode: undefined
      });
    }

    if ($scope.autosaved) {
      $scope.discardAutosaved()
        .then(function () {
          if ($scope.hasPreviousVersion) {
            ContentEditor.openContent($route.current.params.contentId);
          } else {
            $location.url('/');
          }
        })
        .catch(function (error) {
          ErrorMessagingService.sendError(atex.aceui.error.Error({
            type: atex.aceui.error.types.RUNTIME,
            severity: atex.aceui.error.typed.MAJOR,
            origin: COMPONENT_NAME,
            cause: 'Autosaved content could not be discarded.',
            rootCause: error
          }));
        });
    } else {
      if (hasContentId) {
        ContentEditor.openContent($route.current.params.contentId);
      } else {
        $location.url('/');
      }
    }
  };

  $scope.toggleContentHistory = function () {
    $scope.contentHistoryActive = !$scope.contentHistoryActive;
    RecentContentService.updateEditorStateFor(loadData.content.system.id, {
      contentHistoryActive: $scope.contentHistoryActive
    });
  };

  $scope.loadStart = function () {
    $scope.inProgress.increment('loadStart');
    $timeout (() => {
      if ($scope.isLoading) {
        $scope.showSpinner = true;
      }
    }, 100);
    $scope.isLoading = true;
  };

  var savePromise;

  var saveDraft = function () {
    if (savePromise) {
      return savePromise.then(saveToWorkspace);
    } else {
      return saveToWorkspace();
    }
  };

  // Strange name?
  $scope.savePreviewableDraft = function () {
    const contentIdStr = $scope.content.system.id.replace('/', '-');
    const workspaceName = loadData.userId + '-' + contentIdStr + '-preview-workspace';

    return saveToPreviewWorkspace(workspaceName)
      .then((previewContentId) => PreviewService.preview({
        workspace: workspaceName,
        alias: previewContentId
      }))
      .then(({ url }) => {
        $window.open(url, 'code-preview-' + contentIdStr);
      })
      .catch((error) => {
        ErrorMessagingService.sendError(atex.aceui.error.Error({
          type: atex.aceui.error.types.RUNTIME,
          severity: atex.aceui.error.severities.MAJOR,
          origin: COMPONENT_NAME,
          cause: `Error when opening preview: [${error.type}]`,
          rootCause: error
        }));
      });
  };

  $scope.autosaveFn = function () {
    if (!$scope.isDestroyed && !savePromise) {
      if ($scope.hasSaved) {
        debug.log ('Is saved, skipping autosave.');
        return;
      }

      saveDraft()
        .then(() => {
          let hasInvalidFields = $scope.validationContext.validate()
            .filter(result => !result.isValid).length > 0;

          if (!hasInvalidFields && $scope.isPreviewable) {
            return;
          }

          if (hasInvalidFields) {
            $scope.isPreviewable = false;
            return;
          }

          return PreviewService.isPreviewable({
            alias: $scope.content.system.id,
            workspace: loadData.workspace
          })
            .then ((previewable) => {
              $scope.isPreviewable = previewable;
            })
            .catch((error) => {
            // Ignore errors
              console.warn('Failed to determine whether content is previewable or not.', error);
            });
        })
        .catch((error) => {
          ErrorMessagingService.sendError(atex.aceui.error.Error({
            type: atex.aceui.error.types.RUNTIME,
            severity: atex.aceui.error.severities.MAJOR,
            origin: COMPONENT_NAME,
            cause: `Content could not be autosaved to workspace: [${error.type}]`,
            rootCause: error
          }));
        })
        .then(() => savePromise = undefined);
    }
  };

  $scope.copy = function (event) {
    $scope.copyInProgress = true;

    event.stopPropagation();
    event.preventDefault();

    ClipboardService.putInClipboard([
      {
        id: $scope.content.system.id,
        type: $scope.content.system.contentType
      }
    ]).catch(function (error) {
      $(event.target).attr('title', 'Copy failed!');
      $(event.target).tooltip({ 'trigger': 'manual', 'container': 'body' });
      $(event.target).tooltip('show');

      ErrorMessagingService.sendError(atex.aceui.error.Error({
        cause: 'Error: Failed to copy content reference(s) into clipboard!',
        severity: atex.aceui.error.severities.MINOR,
        origin: 'Main View Controller',
        rootCause: error
      }));

      setTimeout(function () {
        $(event.target).tooltip('destroy');
      }, 2000);
    }).then(function () {
      $scope.copyInProgress = false;
    });
  };

  var saveToWorkspace = function () {
    var packedContent = $scope.contentDomainObject.pack();
    var cleanContent = angular.fromJson(angular.toJson(packedContent));

    var writeData = {
      content: cleanContent,
      workspace: loadData.workspace,
      fullTypeInfo: true
    };

    if (!hasContentId && !draftCreated) {
      savePromise = WorkspaceService.createContent(writeData)
        .then(function (result) {
          $scope.content.system.id = atex.aceui.ObjectUtils.getByPath(result, 'content/system/id');
          $rootScope.contentInEdit = $scope.content.system.id;
          draftCreated = true;
        });
    }
    else {
      var workspaceObject = {
        alias: $scope.content.system.id,
        origin: $scope.currentRevision,
        updateMode: 'replace',
        fullTypeInfo: true
      };

      savePromise = WorkspaceService.updateContent(_.extend(writeData, workspaceObject));
    }

    return savePromise.then(function () {
      RecentContentService.addContent($scope.content.system.id, true, true);
      RecentContentService.updateHiddenState($scope.content.system.id, $scope.isHidden);
      $scope.autosaved = true;
      $scope.isDirty = false;
    });
  };

  const saveToPreviewWorkspace = function (workspaceName) {
    const packedContent = $scope.contentDomainObject.pack();
    const cleanContent = angular.fromJson(angular.toJson(packedContent));

    const writeData = {
      content: cleanContent,
      workspace: workspaceName,
      fullTypeInfo: true
    };

    return WorkspaceService.createContent(writeData)
      .then((result) => atex.aceui.ObjectUtils.getByPath(result, 'content/system/id'));
  };

  $scope.discardAutosaved = function () {
    return WorkspaceService.deleteContent({
      workspace: loadData.workspace,
      alias: $scope.content.system.id
    }).then(function () {
      if (!loadData.hasPreviousVersion) {
        RecentContentService.removeContent($scope.content.system.id);
      }
    });
  };

  $scope.selectCounter = 0;

  $scope.versionSelectionChange = function (version) {
    if (version.versionSelected) {
      $scope.selectCounter--;
      version.versionSelected = false;
    } else if ($scope.selectCounter < 2) {
      $scope.selectCounter++;
      version.versionSelected = true;
    }
  };

  $scope.openVersionInModal = function (version, hideEdit = !$scope.permissions.update) {
    let versionObj = $scope.contentHistory.versions.find((v) => v.version === version);

    if (!versionObj) {
      versionObj = { version };
    }

    ContentVersionModal.open($scope, versionObj, $scope.template, $scope.mode === 'view', hideEdit);
    $scope.$on('content-version-modal:edit', function (event, version) {
      ContentEditor.editVersion(version, $scope.currentRevision);
    });
  };

  $scope.openUserInModal = function (userPassport) {
    UserProfileModal.open($scope, userPassport, 'static');
  };

  $scope.compareVersions = function () {
    var selectedVersions = $scope.contentHistory.versions
      .filter(function (version) {
        return version.versionSelected;
      })
      .sort(function (leftValue, rightValue) {
        return leftValue.revision.revisionNumber - rightValue.revision.revisionNumber;
      });

    if (selectedVersions[1].version === '-1') {
      selectedVersions[1].patch = ContentUtils.createContentObject({
        id: $scope.content.system.id,
        contentType: loadData.type,
        creationTime: $scope.content.system.creationTime,
        aspects: $scope.contentDomainObject.pack().aspects
      });
    }

    CompareService.compareVersions(selectedVersions[0], selectedVersions[1]);
  };

  $scope.editPrepare = function (domainObject) {
    $scope.contentDomainObject = domainObject;

    if (hasContentId === false) {
      var taxonomyIdsDomainObject = atex.aceui.DomainObjectUtils.getDomainObjectByPath(domainObject, 'aspects/aceCategorization/taxonomyIds');

      if (typeof taxonomyIdsDomainObject !== 'undefined') {
        var taxonomyIds = taxonomyIdsDomainObject.getData();

        if (taxonomyIds.length === 0) {
          taxonomyIdsDomainObject.setData(['taxonomy']);
        }
      }
    }
  };

  $scope.viewRawData = function ($event, version) {
    $event.stopPropagation();

    if (version.version !== '-1') {
      ContentService.getContentVersion({
        version: version.version,
        atLeastRevision: currentRevisionNumber($scope.currentRevision) || LATEST_REVISION,
        fullTypeInfo: true
      })
        .then(function (result) {
          $window.open('raw-data/raw-data.html', JSON.stringify(result.content));
        })
        .then(undefined, function (error) {
          console.warn('[WARNING] Error while retrieving content JSON!', error);

          var errorObj = {
            version: 'ERROR',
            error: 'Could not retrieve content with version ' + version.version,
            description: error.cause || error.message
          };

          $window.open('raw-data/raw-data.html', JSON.stringify(errorObj));
        });
    }
    else {
      var packedContent = $scope.contentDomainObject.pack();

      var patchObj = ContentUtils.createContentObject({
        id: $scope.content.system.id,
        contentType: loadData.type,
        creationTime: $scope.content.system.creationTime,
        aspects: packedContent.aspects
      });

      $window.open('raw-data/raw-data.html', JSON.stringify(patchObj));
    }
  };

  $scope.publish = function () {
    $scope.saveToServer([{
      _type: 'aceAssignToViews',
      views: [PUBLIC_VIEW]
    }]);
  };

  // if no changes to content,skip the confirmation dialog box and cancel directly
  const confirmCancel = () => {
    if ($scope.isDirty || $scope.autosaved) {
      const modal = $uibModal.open({
        animation: false,
        backdrop: 'static',
        keyboard: false,
        component: 'confirmDialog',
        resolve: {
          confirmText: ($translate) => $translate('content-editor.confirm.cancel-editing.confirm.label'),
          cancelText: ($translate) => $translate('content-editor.confirm.cancel-editing.cancel.label'),
          message: ($translate) => $translate('content-editor.confirm.cancel-editing.text'),
          title: ($translate) => $translate('content-editor.confirm.cancel-editing.title.text')
        }
      });

      modal.result.then(() => {
        $scope.cancelEditing();
      }, () => {});
    } else {
      $scope.cancelEditing();
    }
  };

  const confirmUnpublish = () => {
    const modal = $uibModal.open({
      animation: false,
      backdrop: 'static',
      keyboard: false,
      component: 'confirmDialog',
      resolve: {
        confirmText: ($translate) => $translate('content-editor.confirm.unpublish.confirm.label'),
        cancelText: ($translate) => $translate('content-editor.confirm.unpublish.cancel.label'),
        message: ($translate) => $translate('content-editor.confirm.unpublish.text'),
        title: ($translate) => $translate('content-editor.confirm.unpublish.title')
      }
    });

    modal.result.then(() => {
      $scope.unpublish();
    }, () => {});
  };

  $scope.unpublish = function () {
    $scope.inProgress.increment('unpublish');

    withConflictResolution(function () {
      var requestArgs = {
        alias: $scope.content.system.id,
        revision: $scope.currentRevision,
        view: PUBLIC_VIEW
      };

      return ViewService.unassignFromView(requestArgs).then(function (response) {
        $scope.hasPublicVersion = false;
        $scope.currentRevision = response.revision;
      });
    })
      .catch(function (error) {
        if (error.cancelled) {
          // The publish was cancelled (conflict) - we do nothing
          return;
        }

        ErrorMessagingService.sendError(atex.aceui.error.Error({
          type: atex.aceui.error.types.RUNTIME,
          severity: atex.aceui.error.severities.MAJOR,
          origin: COMPONENT_NAME,
          cause: `Content could not be unpublished [${error.type}]`,
          rootCause: error
        }));
      })
      .then(function () {
        readContentHistory();
        $scope.inProgress.decrement('unpublish');
      });
  };

  $scope.saveToServer = function (operations) {
    operations = operations || [];
    $scope.inProgress.increment('saveToServer');

    var invalidFields = validateRequiredFields();

    if (invalidFields.length > 0) {
      var message = 'Save operation failed. Missing required fields ' + invalidFields.join(', ');

      var error = atex.aceui.error.Error({
        cause: message,
        severity: atex.aceui.error.severities.MAJOR,
        origin: 'Content Developer Main Controller: saveToServer'
      });

      $scope.inProgress.decrement('saveToServer');
      return ErrorMessagingService.sendError(error);
    }

    withConflictResolution(function () {
      return saveDraft()
        .then(function () {
          var requestArgs = {
            alias: $scope.content.system.id,
            workspace: loadData.workspace,
            fullTypeInfo: true
          };

          if (operations.length > 0) {
            requestArgs.operations = operations;
          }

          return WorkspaceService.promoteContent(requestArgs);
        })
        .then(function (result) {
          $scope.autosaved = false;
          $scope.hasSaved = true;

          RecentContentService.addContent(result.content.system.id, true, $scope.autosaved);
          RecentContentService.updateEditorStateFor(result.content.system.id, {
            mode: undefined
          });

          return ContentStateService.getHiddenState(result.content.system.id)
            .then ((isHidden) => {
              RecentContentService.updateHiddenState(result.content.system.id, isHidden);
              return result.content.system.id;
            });
        })
        .then ((aliasOfSaved) => {
          ContentEditor.openContent(aliasOfSaved, true);
        });
    })
      .catch(function (error) {
        $scope.inProgress.decrement('saveToServer');
        savePromise = undefined;

        if (error.cancelled) {
          // The publish was cancelled (conflict) - we do nothing more
          return;
        }

        console.log(error);

        ErrorMessagingService.sendError(atex.aceui.error.Error({
          type: atex.aceui.error.types.RUNTIME,
          severity: atex.aceui.error.severities.MAJOR,
          origin: COMPONENT_NAME,
          cause: `Content could not be saved [${error.type}]`,
          rootCause: error
        }));
      });
  };

  $scope.close = function () {
    RecentContentService.updateEditorStateFor(loadData.content.system.id, {
      mode: undefined
    });

    $location.url('/');
  };

  $scope.updateActiveView = function (parentIndex, index) {
    $scope.activeViewMap[parentIndex] = index;

    RecentContentService.updateEditorStateFor(loadData.content.system.id, {
      activeViewMap: $scope.activeViewMap
    });
  };

  $scope.isActiveView = function (parentIndex, index) {
    if (!$scope.activeViewMap[parentIndex]) {
      return index === 0;
    } else {
      return $scope.activeViewMap[parentIndex] === index;
    }
  };

  let highlightParentTabs = function (tabNavPill) {
    let parentIndexes = $(tabNavPill).attr('data-parentindex');
    let index = parentIndexes.lastIndexOf('-');

    if (index !== -1) {
      let parentIndex = parentIndexes.substring(index + 1);
      let parentTabNavPill = $(tabNavPill).closest('.tab-pane').parent().prev().children()[parentIndex];

      if (parentTabNavPill) {
        $(parentTabNavPill).addClass('invalid-tab');
        highlightParentTabs(parentTabNavPill);
      }
    }
  };

  var validateRequiredFields = function () {
    var invalidFields = [];

    var results = $scope.validationContext.validate();
    if (results.length > 0) {
      $('.inner-editor ul.nav li.tab-pill').removeClass('invalid-tab');

      results.forEach(function (result) {
        var templateField = $('#' + result.widgetId).parent();
        var msgDiv = $('#not-valid-' + result.widgetId);
        var label = $('#field-label-' + result.widgetId + ' h1 span').text();

        var tabNavPill = undefined;
        var index = $(templateField.closest('.tab-pane')).index();

        if (index !== -1) {
          tabNavPill = $(templateField.closest('.tab-pane')).parent().prev().children()[index];
        }

        if (!result.isValid) {
          templateField.addClass('not-valid');

          if (tabNavPill) {
            $(tabNavPill).addClass('invalid-tab');
            highlightParentTabs(tabNavPill);
          }

          var message = (typeof result.message !== 'undefined') ? result.message : label + ' is required';
          msgDiv.html('<span class="not-valid-message">' + message + '</span>');

          invalidFields.push(label);
        } else {
          templateField.removeClass('not-valid');
          msgDiv.html('');
        }
      });
    }

    return invalidFields;
  };

  var withConflictResolution = function (fn) {
    var retryingFn = function () {
      return fn().catch(function (error) {
        let response = JSON.parse(atex.aceui.ObjectUtils.getByPath(error, 'rootCause/responseText'));

        let resolvable =
          error.type === atex.aceui.error.types.CONFLICT
          && response.statusCode === '40900'
          && response.extraInfo.conflictingVersion
          && !response.extraInfo.conflictingVersion.startsWith('draft:');

        if (resolvable) {
          var packedContent = $scope.contentDomainObject.pack();
          var patchObj = ContentUtils.createContentObject({
            id: $scope.content.system.id,
            contentType: loadData.type,
            creationTime: $scope.content.system.creationTime,
            aspects: packedContent.aspects
          });

          return ConflictService.requestConflictResolution(response.extraInfo, patchObj)
            .then(function (resolutionChoice) {
              if (resolutionChoice === ConflictService.CONFLICT_RESOLUTIONS.MINE) {
                // ACE doesn't accept the origin parameter unless it's on the 'eTag' format, so we quote it for now.
                $scope.currentRevision = '"' + response.extraInfo.revision + '"';

                return $timeout(retryingFn);
              } else if (resolutionChoice === ConflictService.CONFLICT_RESOLUTIONS.SERVER) {
                return $scope.cancelEditing();
              } else if (resolutionChoice === ConflictService.CONFLICT_RESOLUTIONS.POSTPONE) {
                return $q.reject({ cancelled: true });
              }
            });
        } else {
          return $q.reject(error);
        }
      });
    };

    return retryingFn();
  };

  $scope.navigateToField = function (id) {
    const positionTop = $('#field-label-' + id).position().top;
    const scrollTop = $('.editor-column').scrollTop();
    const heightToolbar = $('.code-ui-view .toolbar').outerHeight(true);
    const heightHeader = $('.code-ui .head').outerHeight(true);

    const heightContentInfo = $('.content-information-container').outerHeight(true);
    const offset = heightToolbar + heightHeader + heightContentInfo;

    const animationProperties = {
      scrollTop: positionTop + scrollTop - offset
    };

    $('.editor-column').animate(animationProperties, 400);
  };

  $scope.hasLayout = () => {
    return !!($scope.layout && $scope.layout.views && $scope.layout.views.length);
  };

  $scope.loadEnd = function (context) {
    if (typeof $scope.contentHistoryActive !== 'undefined') {
      $scope.contentHistoryActive = existingEditorState.contentHistoryActive;
    }

    if (!$scope.isEditMode) {
      RecentContentService.updateEditorStateFor(loadData.content.system.id, {
        draftCreationTime: undefined
      });
    }

    if (typeof $scope.activeViewMap !== 'undefined') {
      $scope.activeViewMap = existingEditorState.activeViewMap || $scope.activeViewMap;
    }

    $scope.fields = context.fields;
    $scope.layout = context.layout;

    if ($scope.fields.length > 0) {
      $scope.currentField = 'field-label-' + $scope.fields[0].widgetId;
    }

    let updateEditorHeight = () => {
      var lastTemplateField = $('.template-field').last(),
        missingPadding = $('.inner-editor').height() - lastTemplateField.height();

      if (missingPadding > 0) {
        $('.main-view').css('margin-bottom', missingPadding);
      }
    };

    if ($scope.hasLayout()) {
      $scope.isLoading = false;
      $scope.showSpinner = false;
      $scope.inProgress.decrement('loadStart');
    } else {
      $(window).on('resize', function () {
        updateEditorHeight();
      });

      if (typeof $scope.currentField !== 'undefined') {
        /*
         * Scroll Spy
         */
        $timeout(function () {
          let elements = $scope.fields.map(function (field) {
            return $('#' + field.widgetId)[0];
          });

          let editorColumnContainer = angular.element('.editor-column');

          let padding = .9;
          let hotspotOffset = 50;
          let pleaseUpdateEditorHeight = true;

          $scope.isLoading = false;
          $scope.showSpinner = false;
          $scope.inProgress.decrement('loadStart');

          updateEditorHeight();

          editorColumnContainer.scroll(function () {
            let hotspot = editorColumnContainer.offset().top + hotspotOffset;
            let spyArea = (editorColumnContainer.offset().top + editorColumnContainer.height()) * padding;

            let nearest = _.chain(elements)
              .filter(function (el) {
                return spyArea > $(el).offset().top;
              })
              .reduce(function (a, b) {
                return Math.abs($(a).offset().top - hotspot) < Math.abs($(b).offset().top - hotspot) ? a : b;
              })
              .value();

            if (nearest) {
              $scope.$apply(function () {
                $scope.currentField = 'field-label-' + $(nearest).attr('id');
              });
            }

            if (pleaseUpdateEditorHeight) {
              $scope.$apply(function () {
                updateEditorHeight();
                pleaseUpdateEditorHeight = false;
              });
            }
          });
        });
      }
    }
  };

  var getSecurityContexts = function (metadata) {
    var securityContexts = atex.aceui.ObjectUtils.getByPath(
      metadata,
      'aspects/aceSecurityContext/owners');

    return securityContexts || [];
  };

  function readPermissions (type, securityContexts) {
    var hasPermission = function (permission) {
      return AuthorizationService.hasPermission(permission, type, securityContexts);
    };

    return $q.all({
      assignToView: hasPermission('AssignToView'),
      contentMetadata: hasPermission('ContentMetadata'),
      create: hasPermission('Create'),
      hasSuperpowers: AuthorizationService.hasSuperpowers (),
      unassignFromView: hasPermission('UnassignFromView'),
      update: hasPermission('Update')
    })
      .then(function (permissions) {
        return {
          create: permissions.create,
          update: permissions.update,

          publish: permissions.assignToView
                   && permissions.contentMetadata,

          unpublish: permissions.unassignFromView
                     && permissions.contentMetadata,

          hide: permissions.update
                && permissions.contentMetadata,

          sudo: permissions.hasSuperpowers,
        };
      });
  }

  $scope.securityContexts = getSecurityContexts(loadData.metadata);

  readPermissions($scope.template.type, $scope.securityContexts)
    .then(function (permissions) {
      $scope.permissions = permissions;
    });

  let infoToggleShortcut = {
    action: function (event) {
      $scope.$apply(function () {
        event.stopPropagation();
        event.preventDefault();
        $scope.toggleContentHistory();
      });
    },
    label: 'keyboard-shortcuts.toggle-history.label'
  };

  if ($scope.isEditMode === true) {
    KeyboardShortcutService.push({
      global: {
        'mod+i': infoToggleShortcut,
        'mod+s': {
          action: function (event) {
            $scope.$apply(function () {
              event.stopPropagation();
              event.preventDefault();
              $scope.saveToServer();
            });
          },
          label: 'keyboard-shortcuts.save-content.label'
        },
        'escape': {
          action: function (event) {
            $scope.$apply(function () {
              event.stopPropagation();
              event.preventDefault();
              confirmCancel();
            });
          },
          label: 'keyboard-shortcuts.cancel-editing.label'
        }
      }
    });
  }
  else {
    KeyboardShortcutService.push({
      global: {
        'mod+i': infoToggleShortcut,
        'mod+e': {
          action: function (event) {
            if ($scope.permissions && $scope.permissions.update) {
              $scope.$apply(function () {
                event.stopPropagation();
                event.preventDefault();
                $scope.startEditing();
              });
            }
          },
          label: 'keyboard-shortcuts.edit-content.label'
        },
        'mod+u': {
          action: function (event) {
            $scope.$apply(function () {
              event.stopPropagation();
              event.preventDefault();
              $scope.close();
            });
          },
          label: 'keyboard-shortcuts.close-content.label'
        }
      }
    });
  }

  $scope.workflowTransition = (transition) => {
    $scope.inProgress.increment('workflowTransition');
    ContentEventService.pause([WORKFLOW_SUBSCRIPTION, UPDATE_SUBSCRIPTION]);

    CodeWorkflow.transition (transition, $scope)
      .then (() => $route.reload())
      .catch ((error) => {
        if (error.type !== atex.aceui.error.types.OPERATION_CANCELLED) {
          if (error.type === atex.aceui.error.types.CONFLICT) {
            ErrorMessagingService.sendError(atex.aceui.error.Error({
              cause: 'Content conflict! You must reload the content before performing transition.',
              type: error.type,
              rootCause: error,
              severity: atex.aceui.error.severities.MAJOR
            }), {
              'code.reload': () => $route.reload()
            });
          } else {
            ErrorMessagingService.sendError(error);
          }
        }

        $scope.inProgress.decrement('workflowTransition');
        ContentEventService.resume([WORKFLOW_SUBSCRIPTION, UPDATE_SUBSCRIPTION]);
      });
  };

  const confirmAbortWorkflow = () => {
    const modal = $uibModal.open({
      animation: false,
      backdrop: 'static',
      keyboard: false,
      component: 'confirmDialog',
      resolve: {
        confirmText: ($translate) => $translate('content-editor.workflow.abort-dialog.confirm.text'),
        cancelText: ($translate) => $translate('content-editor.workflow.abort-dialog.cancel.text'),
        message: ($translate) => $translate('content-editor.workflow.abort-dialog.message.text'),
        title: ($translate) => $translate('content-editor.workflow.abort-dialog.title')
      }
    });

    modal.result.then(() => {
      $scope.abortWorkflow();
    }, () => {});
  };

  $scope.abortWorkflow = function () {
    $scope.inProgress.increment('abortWorkflow');
    WorkflowService.abort ({
      revision: $scope.currentRevision,
      workflowId: $scope.workflow.name,
      alias: $scope.content.system.id,
    })
      .then ((workflowResult) => {
        debug.log ('Workflow abort success!', workflowResult);
        $route.reload();
      })
      .catch ((error) => {
        ErrorMessagingService.sendError(error);
        $scope.inProgress.decrement('abortWorkflow');
      });
  };

  $scope.triggerAbortWorkflow = function () {
    confirmAbortWorkflow();
  };

  $scope.takeAction = function (action) {
    switch (action.name) {
    case 'cancel':
      ErrorMessagingService.clearErrors();
      $scope.cancelEditing();
      return;
    case 'close':
      ErrorMessagingService.clearErrors();
      $scope.close();
      return;
    case 'copy':
      $scope.copy(action.event);
      return;
    case 'edit':
      ErrorMessagingService.clearErrors();
      $scope.startEditing();
      return;
    case 'info':
      $scope.toggleContentHistory();
      return;
    case 'preview':
      $scope.savePreviewableDraft();
      return;
    case 'publish':
      ErrorMessagingService.clearErrors();
      $scope.publish();
      return;
    case 'save':
      ErrorMessagingService.clearErrors();
      $scope.saveToServer();
      return;
    case 'unpublish':
      ErrorMessagingService.clearErrors();
      confirmUnpublish();
      return;
    case 'transition':
      $scope.workflowTransition (action.transition);
      return;
    }
  };

  $scope.$on('$destroy', function () {
    KeyboardShortcutService.pop();
    delete $rootScope.contentInEdit;
    delete $rootScope.contentObjectInEdit;
  });
}

// **************************
//  Main Controller resolves
// **************************

ContentDeveloperMainController.resolves = function (openFor) {
  let mode = undefined;

  switch (openFor) {
  case 'view':
    mode = 'view';
    break;

  case 'edit':
    mode = 'edit';
    break;

  case 'version':
    mode = 'view';
    break;
  }

  function resolveWorkflowFn (loadData, CodeWorkflow) {
    return CodeWorkflow.getContextualWorkflow(loadData)
      .then((workflow) => {
        loadData.workflow = workflow;
        return loadData;
      });
  }

  function createContentResolveFn (
    $q, $rootScope, $route, AuthenticationService,
    CodeWorkflow, ContentEditor, TemplateService, ErrorMessagingService) {
    let loadData = {};

    delete $rootScope.contentObjectInEdit;

    function resolveWorkflow (loadData) {
      return resolveWorkflowFn(loadData, CodeWorkflow, ErrorMessagingService);
    }

    return AuthenticationService.getCurrentUser()
      .then (function (user) {
        loadData.user = user;
        return TemplateService.getTemplate($route.current.params.type, 'Create');
      })
      .then (function (template) {
        if (!template) {
          return $q.reject(atex.aceui.error.Error({
            origin: 'Content Developer Main Controller Resolve',
            type: atex.aceui.error.types.NOT_FOUND,
            severity: atex.aceui.error.severities.CRITICAL,
            cause: 'No template \'' + $route.current.params.type + '\' was found! Cannot create content...'
          }));
        }

        loadData.template = template;
        loadData.type = template.type;

        return template;
      })
      .then (function () {
        let content = atex.aceui.Content();

        content.system = {
          contentType: loadData.template.type
        };

        return ContentEditor.onContentInit(content)
          .then(function (result) {
            content = result;

            loadData.content = content;
            loadData.type =  content.system.contentType;

            return content.system;
          })
      })
      .then (() => {
        return resolveWorkflow(loadData);
      })
      .then (function () {
        return {
          content: loadData.content,
          template: loadData.template,
          workspace: loadData.user.userId + '-workspace',
          userId: loadData.user.userId,
          mode: 'edit',
          workflow: loadData.workflow
        };
      });
  }

  function contentVersionResolveFn (
    $q, $route, AuthenticationService, CodeWorkflow, ContentEditor,
    ContentService, PreviewService, TemplateService, ErrorMessagingService) {

    function resolveUser (loadData) {
      return AuthenticationService.getCurrentUser()
        .then (function (user) {
          loadData.userId = user.userId;
          loadData.workspace = user.userId + '-workspace';
          return loadData;
        });
    }

    function resolveWorkflow (loadData) {
      return resolveWorkflowFn(loadData, CodeWorkflow, ErrorMessagingService);
    }

    function resolveContent (loadData) {
      return ContentService.getContentVersion({
        version: $route.current.params.versionId,
        fullTypeInfo: true,
        atLeastRevision: LATEST_REVISION
      })
        .then (function (result) {
          loadData.content = result.content;
          loadData.type = result.content.aspects._type || result.content.system.contentType;

          return result.content.system.id;
        })
        .then (function (contentId) {
        // NOTE: We try to fetch the content from the ContentService to see
        // if it has a previous version, because we cannot tell that from the
        // WorkspaceService result.

          return ContentService.getContent({ alias: contentId, fullTypeInfo: true, atLeastRevision: currentRevisionNumber(loadData.revision) || LATEST_REVISION });
        })
        .then (function () {
          loadData.hasPreviousVersion = true;
          return loadData;
        }, function (error) {
          if (error
                && error.origin === 'atex.aceui.service.ContentService'
                && error.type === atex.aceui.error.types.NOT_FOUND) {
            loadData.hasPreviousVersion = false;
            return loadData;
          }
          return $q.reject (error);
        });
    }

    function resolveTemplate (loadData) {
      var contentType = loadData.content.system.contentType,
        permission = loadData.mode === 'view' ? 'Read' : 'Update';

      return TemplateService.getTemplates(permission, contentType)
        .then (function (templateList) {
          if (templateList && templateList.length) {
            loadData.template = templateList[0];
          } else {
            return $q.reject(atex.aceui.error.Error({
              origin: 'Content Developer Main Controller Resolve: resolveTemplate',
              type: atex.aceui.error.types.NOT_FOUND,
              severity: atex.aceui.error.severities.CRITICAL,
              cause: 'No template found for content type "' + contentType + '".'
            }));
          }

          return loadData;
        });
    }

    function resolvePreviewable (loadData) {
      return PreviewService.isPreviewable({
        alias: loadData.content.system.id,
        workspace: loadData.workspace
      })
        .then((previewable) => {
          loadData.isPreviewable = previewable;
          return loadData;
        });
    }

    function resolveContentMetadata (loadData) {
      if (loadData.hasPreviousVersion) {
        return ContentService.getContentMetadata ({ alias: loadData.content.system.id, atLeastRevision: currentRevisionNumber(loadData.revision) || LATEST_REVISION })
          .then (function (result) {
            loadData.metadata = result.metadata;

            return loadData;
          });
      } else {
        return loadData;
      }
    }

    return resolveUser ({ mode: mode })
      .then (resolveContent)
      .then (resolvePreviewable)
      .then (resolveContentMetadata)
      .then (resolveTemplate)
      .then (resolveWorkflow);
  }

  function contentResolveFn (
    $route, $q, WorkspaceService, TemplateService, ApplicationConfigurationService, AuthenticationService,
    CodeWorkflow, ContentService, ContentEditor, PreviewService, ErrorMessagingService) {

    function resolveUser (loadData) {
      return AuthenticationService.getCurrentUser()
        .then (function (user) {
          loadData.user = user;
          loadData.userId = user.userId;
          loadData.workspace = user.userId + '-workspace';

          return loadData;
        });
    }

    function resolveContent (loadData) {
      const { mode, workspace } = loadData;
      return WorkspaceService.getContent({
        alias: $route.current.params.contentId,
        workspace,
        fullTypeInfo: true,
        atLeastRevision: LATEST_REVISION
      })
        .then (function (result) {
          const { content, revision } = result;

          loadData.content = content;
          loadData.revision = revision;
          loadData.autosaved = (typeof revision === 'undefined');
          loadData.type = content.aspects._type || content.system.contentType;

          if (loadData.autosaved && mode === 'view') {
            ContentEditor.editContent(content.system.id);
          }

          return content.system.id;
        })
        .then (function (contentId) {
          // NOTE: We try to fetch the content from the ContentService to see
          // if it has a previous version, because we cannot tell that from the
          // WorkspaceService result.

          return ContentService.getContent({ alias: contentId, fullTypeInfo: true, atLeastRevision: currentRevisionNumber(loadData.revision) || LATEST_REVISION });
        })
        .then (function () {
          loadData.hasPreviousVersion = true;
          return loadData;
        }, function (error) {
          if (error
              && error.origin === 'atex.aceui.service.ContentService'
              && error.type === atex.aceui.error.types.NOT_FOUND) {
            loadData.hasPreviousVersion = false;
            return loadData;
          } else {
            return $q.reject (error);
          }
        });
    }

    function resolveTemplate (loadData) {
      const { content, mode } = loadData;
      const contentType = content.system.contentType,
        permission = mode === 'view' ? 'Read' : 'Update';

      return TemplateService.getTemplates(permission, contentType)
        .then (function (templateList) {
          if (templateList && templateList.length) {
            loadData.template = templateList[0];
          } else {
            return $q.reject(atex.aceui.error.Error({
              origin: 'Content Developer Main Controller Resolve: resolveTemplate',
              type: atex.aceui.error.types.NOT_FOUND,
              severity: atex.aceui.error.severities.CRITICAL,
              cause: 'No template found for content type "' + contentType + '".'
            }));
          }

          return loadData;
        })
        .catch ((error) => {
          return $q.reject(atex.aceui.error.Error({
            origin: 'Content Developer Main Controller Resolve: resolveTemplate',
            type: atex.aceui.error.types.RUNTIME,
            severity: atex.aceui.error.severities.CRITICAL,
            cause: 'Error getting templates from server.',
            rootCause: error
          }));
        });
    }

    function resolvePreviewable (loadData) {
      const { content, workspace } = loadData;
      return PreviewService.isPreviewable({
        alias: content.system.id,
        workspace
      })
        .then((previewable) => {
          loadData.isPreviewable = previewable;
          return loadData;
        });
    }

    function resolveCloneability (loadData) {
      return ApplicationConfigurationService.getConfigurationsFor (['ContentCloning'])
        .then (([config]) => {
          loadData.isCloneable = (config?.contentTypes || []).includes(loadData.template.type)
              && loadData.hasPreviousVersion

          return loadData;
        });
    }

    function resolveContentMetadata (loadData) {
      const { content, hasPreviousVersion } = loadData;
      if (hasPreviousVersion) {
        return ContentService.getContentMetadata ({ alias: content.system.id, atLeastRevision: currentRevisionNumber(loadData.revision) || LATEST_REVISION })
          .then (function (result) {
            loadData.metadata = result.metadata;
            return loadData;
          });
      } else {
        return loadData;
      }
    }

    function resolveWorkflow (loadData) {
      return resolveWorkflowFn(loadData, CodeWorkflow, ErrorMessagingService);
    }

    return resolveUser ({ mode: mode })
      .then (resolveContent)
      .then ((loadData) => {
        return $q.all([
          resolvePreviewable(loadData),
          resolveContentMetadata(loadData),
          resolveTemplate(loadData)
        ]).then(() => loadData);
      })
      .then (resolveCloneability)
      .then (resolveWorkflow)
      .then ((loadData) => {
        if (_.has(loadData, 'workflow.state')
            && !loadData.workflow.state.isEditableBy
            && loadData.mode === 'edit') {
          ContentEditor.openContent(loadData.content.system.id, true, 'view');
        }
        return loadData;
      });
    /*
      .then (resolvePreviewable)
      .then (resolveContentMetadata)
      .then (resolveTemplate)
      .then (resolveWorkflowDefinition); */
  }

  if (openFor === 'create') {
    return {
      loadData: createContentResolveFn
    };
  } else if (openFor === 'version') {
    return {
      loadData: contentVersionResolveFn
    };
  } else {
    return {
      loadData: contentResolveFn
    };
  }
};
